import { ChangeDetectionStrategy, Component, OnInit, Signal, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideArrowRightLeft, lucideCircleAlert, lucideCircleHelp, lucideClock, lucideGoal, lucideHousePlus, lucideKeyRound, lucidePhone, lucideShieldX } from '@ng-icons/lucide';

import { FocusOnlyRadioGroupDirective } from 'app/home/common/directives/focus-only-radio-group.directive';
import { ReadonlyRadioDirective } from 'app/home/common/directives/readonly-radio.directive';
import { BaseQuoteForm } from 'app/home/create-quote/form-config/quote-form-base';
import { QuoteFormContextDecorator } from 'app/home/create-quote/form-config/quote-form-decorator';
import { Lockable, LockedQuoteAlertComponent, WithLockableFields } from 'app/home/create-quote/form-locking';
import { InsuranceStatusQuoteFormInput, InsuranceStatusValue } from 'app/home/create-quote/forms/insurance-status/insurance-status.types';
import { FormCardComponent } from 'app/home/create-quote/forms/shared/form-card.component';
import { QuoteDraftState } from 'app/home/global-store/quote-draft/quote-draft.model';
import { QuoteFormOptionsStore } from 'app/home/global-store/quote-form-options';
import * as cc from 'app/shared/constants/contact-constants';
import { GoogleTagManagerService, SegmentEvent } from 'app/shared/services/analytics/google-tagmanager/gtm.service';
import { FeatureFlagSignalsService } from 'app/shared/services/splitIO/feature-flag.service';

@Component({
  selector: 'app-insurance-status',
  imports: [ReactiveFormsModule, NgIconComponent, FormCardComponent, ReadonlyRadioDirective, LockedQuoteAlertComponent, FocusOnlyRadioGroupDirective],
  templateUrl: './insurance-status.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [provideIcons({ lucideArrowRightLeft, lucideShieldX, lucideHousePlus, lucideKeyRound, lucideClock, lucideGoal, lucideCircleHelp, lucideCircleAlert, lucidePhone })],
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
})
@QuoteFormContextDecorator({
  legacyShape: '/quote/insurance_status',
  isAutoAdvanced: true,
})
export class QuoteFormInsuranceStatusComponent extends BaseQuoteForm<InsuranceStatusQuoteFormInput> implements OnInit, Lockable {
  private featureFlagSignalsService = inject(FeatureFlagSignalsService);
  public isAutoAdvanceEnabled = this.featureFlagSignalsService.isAutoAdvanceEnabled;

  private optionsStore = inject(QuoteFormOptionsStore);
  private gtmService = inject(GoogleTagManagerService);
  public formDataIn: InsuranceStatusQuoteFormInput = this.quoteDraftStore.insuranceStatusQuoteFormSelector();

  public insuranceStatusOptions = this.optionsStore.insuranceStatusQuoteFormOptionsSelector();

  public formDefinition = {
    insuranceStatus: new FormControl<InsuranceStatusValue | undefined>(undefined, [Validators.required]),
  };
  public insuranceStatusForm = new FormGroup(this.formDefinition);

  public salesPhoneNumber = cc.PHONE_NUMBERS.sales;
  public salesPhoneHoursFormatted = cc.PHONE_HOURS_FORMATTED.sales;

  public override autoAdvanceControlRef = this.insuranceStatusForm.controls.insuranceStatus;

  public ngOnInit() {
    if (this.formDataIn && this.isFieldLocked('insuranceStatus')) {
      this.insuranceStatusForm.controls.insuranceStatus.setValue(this.formDataIn.insuranceStatus);
    }
    this.gtmService.sendDataLayerEventWithQuoteData(SegmentEvent.INSURANCE_INFO_PAGE_VIEWED);
  }

  public getFormControls() {
    return this.insuranceStatusForm.controls;
  }

  public isLocked: Signal<{ [key: string]: boolean }> = WithLockableFields.createIsLocked.call(this);
  public isFieldLocked = WithLockableFields.isFieldLocked;
  public allFieldsLocked = WithLockableFields.allFieldsLocked;

  private getFormValues(): Pick<QuoteDraftState, 'insuranceStatus'> {
    const { controls } = this.insuranceStatusForm;
    const selectedId = controls.insuranceStatus.value;
    const formValues: Pick<QuoteDraftState, 'insuranceStatus'> = {
      insuranceStatus: selectedId as InsuranceStatusValue,
    };

    return formValues;
  }

  public get showError(): boolean {
    if (this.isAutoAdvanceEnabled()) {
      return false;
    }

    const control = this.insuranceStatusForm.get('insuranceStatus');
    return !!control?.invalid && !!control?.touched;
  }

  public handleSubmit(e: Event) {
    e.preventDefault();
    this.insuranceStatusForm.markAllAsTouched();
    if (this.insuranceStatusForm.valid) {
      if (this.allFieldsLocked()) {
        super.navigateForward();
      } else {
        this.gtmService.sendDataLayerEventWithQuoteData(SegmentEvent.INSURANCE_INFO_PAGE_SUBMITTED, this.getFormValues());
        super.saveFormData(this.getFormValues());
      }
    }
  }
}
